import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, ArrowLeft } from "lucide-react";
import { PageHeader, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function CreateCircuit() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const handleCreate = async () => {
    if (name.trim().length < 3) {
      toast.error("Circuit name must be at least 3 characters");
      return;
    }

    setBusy(true);
    try {
      const { data } = await api.post("/arena/circuits/create", {
        name: name.trim(),
        description: description.trim()
      });

      toast.success(data.message, { icon: <Trophy size={16} /> });
      navigate("/arena-circuit/my-circuit");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/arena-circuit/circuits")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
        >
          <ArrowLeft size={16} />
        </button>
        <PageHeader
          icon={Trophy}
          title="Create Arena Circuit"
          subtitle="Organize fight cards and manage gladiators"
        />
      </div>

      <div className="border border-amber-900/30 bg-zinc-950 p-6 space-y-6">
        {/* Circuit Name */}
        <div>
          <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">
            Circuit Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            placeholder="Enter circuit name (3-50 characters)"
            className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-zinc-100 focus:border-amber-600 outline-none"
          />
          <div className="text-xs text-zinc-600 mt-1">{name.length} / 50 characters</div>
        </div>

        {/* Description */}
        <div>
          <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={4}
            placeholder="Describe your circuit's style, rules, or focus..."
            className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-zinc-100 focus:border-amber-600 outline-none resize-none"
          />
          <div className="text-xs text-zinc-600 mt-1">{description.length} / 500 characters</div>
        </div>

        {/* Info */}
        <div className="bg-amber-950/20 border border-amber-700/40 rounded p-4">
          <h4 className="font-cinzel text-amber-300 font-semibold mb-2">What You Can Do:</h4>
          <ul className="text-sm text-zinc-300 space-y-1">
            <li>• Recruit gladiators by offering contracts</li>
            <li>• Organize fight cards and tournaments</li>
            <li>• Pay gladiators upfront with gold escrow</li>
            <li>• Build reputation and prestige</li>
          </ul>
        </div>

        {/* Create Button */}
        <button
          onClick={handleCreate}
          className={btn() + " w-full"}
          disabled={busy || name.trim().length < 3}
        >
          <Trophy size={16} className="mr-2" />
          {busy ? "Creating..." : "Create Circuit"}
        </button>
      </div>

      {/* Guidelines */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded p-4">
        <h4 className="font-cinzel text-zinc-300 text-sm mb-3 uppercase tracking-wide">
          Circuit Guidelines
        </h4>
        <ul className="text-xs text-zinc-500 space-y-2">
          <li>• Circuits close automatically after 14 days of inactivity</li>
          <li>• You can only have one active circuit per season</li>
          <li>• Circuit names must be unique within the season</li>
          <li>• Treat fighters fairly and maintain good reputation</li>
        </ul>
      </div>
    </div>
  );
}
